import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemAvatar,
  Skeleton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { memo, useEffect, useState } from "react";
import { ComponentProps, NotificationDataType } from "./types"
import { useAppDispatch, useAppSelector } from "../../store/store";
import moment from "moment";
import { getRecentNotificationData } from "../../store/thunk/mainDashboardThunk";
import { useNavigate } from "react-router-dom";
import { DateTimeFormat } from "../common/DateFormat";

const RecentNotification = ({ id, index, pin, pinComponentHandler, selectedValueFacility, facilityIds, locationIds, currentTenant, ...rest }: ComponentProps) => {

  const { data: notifications, loading } = useAppSelector((store) => store?.mainDashboard?.recentNotification);
  const { userData } = useAppSelector((store) => store?.authReducer);
  const locationLoader = useAppSelector(store => store?.locationSlice?.loading);
  const tenantLoader = useAppSelector(store => store?.tenantSlice?.loading);
  const substanceDetails = useAppSelector(store => store?.substanceSlice?.substanceConfigurationList);
  const [loader, setLoader] = useState(true);
  const navigate = useNavigate();
  useEffect(() => {
    setLoader(loading || locationLoader || tenantLoader);
  }, [loading, locationLoader, tenantLoader]);
  const dispatch = useAppDispatch();
  useEffect(() => {
    const facilityKey = facilityIds?.replace("all,", "");
    if (/* currentTenant?.tenantCode &&  */(facilityKey || locationIds /* || userData?.group === 'admin' */))
      dispatch(getRecentNotificationData({ tenantId: currentTenant?.tenantId as string, facilityKey: facilityKey, locationId: locationIds }))
  }, [facilityIds, locationIds, /* currentTenant?.tenantId, userData?.group */]);

  const redirectToQuery = (notificationInfo: NotificationDataType) => {
    navigate("/redirect-dashboard", {
      state: { notificationData: notificationInfo },
    });
  }

  return (
    <Card sx={{
      borderRadius: "3px !important",
      border: "none",
      backgroundColor: "#fff !important",
      boxShadow: "0px 2px 4px 0px rgba(0, 0, 0, 0.1) !important",
      height: "100%",
      marginRight: '0px',
      width: "100%",
      padding: "0px !important"
    }}>
      <Box onClick={() => pinComponentHandler(id)} sx={{ width: "20px", position: 'absolute', display: 'flex', justifyContent: 'flex-end', top: 14, right: 14, cursor: "pointer", zIndex: 11 }}>
        {pin ? <img src="./assets/images/pin-filled.png" alt="pin" width={15} /> : <img src="./assets/images/pin-outline.png" alt="pin" width={15} />}
      </Box>
      <CardContent
        sx={{
          maxHeight: 450,
          overflowY: "auto",
          p: 2,
          pt: 0,
          backgroundColor: "#fff",
          // Custom scrollbar styles for WebKit
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#c1c1c1",
            borderRadius: "3px",
          },
          // Firefox scrollbar styling
          scrollbarWidth: "thin",
        }}
      >
        {/* Sticky header for title */}
        <Box
          className="title"
          {...(pin ? {} : rest)}
          sx={{
            py: 2,
            position: "sticky",
            top: 0,
            zIndex: 10,
            backgroundColor: "#fff",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: "bold",
              transition: "background-color 0.3s, border-radius 0.3s",
              // Only add hover effects if not pinned
              ":hover": !pin
                ? {
                  backgroundColor: "#f4f4f4",
                  borderRadius: "10px",
                  cursor: "grab",
                }
                : {},
            }}
          >
            Recent Notifications
          </Typography>
        </Box>

        <List sx={{ px: "2px" }}>
          {notifications.map((item, index) => {
            const substance = substanceDetails?.find((value: any) => value?.substanceKey === item?.substance);
            return (
              <ListItem
                key={index}
                onClick={() => redirectToQuery(item)}
                sx={{
                  backgroundColor: "#f9f9f9",
                  borderRadius: "8px",
                  mb: 1,
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                  },
                  cursor: "pointer",
                }}
              >
                <ListItemAvatar>
                  <StatusDot colorCode={item?.colorCode} />
                </ListItemAvatar>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      display: "block",
                      fontWeight: "bold",
                      textTransform: "capitalize",
                    }}
                  >
                    Characteristics: {substance?.displayName || item?.substance}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      display: "block",
                      color: "#a6acb8",
                    }}
                  >
                    Status: {item.status}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      display: "block",
                      color: "#a6acb8",
                    }}
                  >
                    Location: {item.location}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#a6acb8" }}>
                    {moment
                      ?.unix(item?.sentTime / 1000)
                      ?.utc(false)
                      ?.tz(userData?.timezone || moment.tz.guess())
                      .format(DateTimeFormat())}
                  </Typography>
                </Box>
              </ListItem>
            )
          })}

          {/* Display message when no notifications are available */}
          {!notifications?.length && !loader && (
            <ListItem
              key="no-data"
              sx={{
                backgroundColor: "#f9f9f9",
                borderRadius: "8px",
                mb: 1,
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                display: "flex",
                justifyContent: "center",
                py: 2,
              }}
            >
              <Typography variant="body2" sx={{ color: "#777" }}>
                Notifications are not available
              </Typography>
            </ListItem>
          )}

          {/* Display skeletons when loading */}
          {loader &&
            [...new Array(3)].map((_, index) => (
              <ListItem
                key={index}
                sx={{
                  backgroundColor: "#f9f9f9",
                  borderRadius: "8px",
                  mb: 1,
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                }}
              >
                <ListItemAvatar>
                  <Skeleton variant="circular" width={50} height={50} />
                </ListItemAvatar>
                <Box sx={{ width: "100%" }}>
                  <Skeleton variant="text" width={150} height={25} />
                  <Skeleton variant="text" width={100} height={20} />
                  <Skeleton variant="text" width={100} height={20} />
                </Box>
              </ListItem>
            ))}
        </List>
      </CardContent>
    </Card>
  )
}

export default memo(RecentNotification);
const StatusDot = styled("div")<{ colorCode: string }>(({ colorCode }) => ({
  width: "30px",
  height: "30px",
  borderRadius: "50%",
  backgroundColor: colorCode,
}));